<script setup lang="ts">
    import { ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import { Permissions } from '@shared/Root/Permissions';
    import Collapse from '@shared/components/Collapse.vue';

    export interface SidebarMenuItem {
        routeNames: string[];
        label: string;
        requireScope?: { Feature: string; Permission: Permissions };
        feature?: string;
    }

    export interface SidebarMenuGroup {
        heading?: string;
        children?: SidebarMenuItem[];
        submenu?: boolean;
        routeNames?: string[];
        label?: string;
        requireScope?: { Feature: string; Permission: Permissions };
        feature?: string;
        /** Material Symbols path data (viewBox 0 -960 960 960) */
        icon?: string;
    }

    const props = withDefaults(
        defineProps<{
            items: SidebarMenuGroup[];
            params?: Record<string, string>;
        }>(),
        {
            params: () => ({}),
        }
    );

    const router = useRouter();

    const isCurrent = (routeNames: string[]) => {
        const currentPathObject = router.currentRoute.value;
        return routeNames.includes(currentPathObject.name?.toString() || '');
    };

    const initialActiveGroups = () => {
        const currentName = router.currentRoute.value.name?.toString() || '';
        const groups: Record<string, boolean> = {};
        for (const item of props.items) {
            if (item.heading && item.children?.some((child) => child.routeNames.includes(currentName))) {
                groups[item.heading] = true;
            }
        }
        return groups;
    };

    const activeGroups = ref<Record<string, boolean>>(initialActiveGroups());

    // Sync open submenus with the current route so navigating elsewhere closes them
    watch(
        () => router.currentRoute.value.name,
        () => (activeGroups.value = initialActiveGroups())
    );

    const toggleGroup = (heading: string) => {
        const wasActive = activeGroups.value[heading];
        activeGroups.value = {};
        activeGroups.value[heading] = !wasActive;
    };

    const iconSize = 18;
</script>

<template>
    <ul class="tabs list-unstyled display-row">
        <template v-for="item in items" :key="item.heading ?? item.routeNames?.[0]">
            <li
                v-if="item.children"
                v-require-scope="item.requireScope"
                v-feature="item.feature"
                class="sidebar-group"
                :class="{ active: item.heading && item.submenu && activeGroups[item.heading] }">
                <template v-if="item.heading">
                    <a
                        v-if="item.submenu"
                        href="#"
                        role="button"
                        :aria-expanded="!!activeGroups[item.heading]"
                        :aria-controls="`sidebar-submenu-${item.heading}`"
                        @click.prevent="toggleGroup(item.heading)"
                        class="d-flex justify-content-between align-items-center">
                        <span class="d-flex align-items-start gap-xs">
                            <svg
                                v-if="item.icon"
                                class="sidebar-icon"
                                xmlns="http://www.w3.org/2000/svg"
                                :width="iconSize"
                                :height="iconSize"
                                viewBox="0 -960 960 960"
                                fill="currentColor"
                                aria-hidden="true">
                                <path :d="item.icon"></path>
                            </svg>
                            {{ item.heading }}
                        </span>
                        <svg
                            class="sidebar-chevron"
                            :class="{ collapsed: !activeGroups[item.heading] }"
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 -960 960 960"
                            aria-hidden="true">
                            <path
                                d="M480-528 324-372q-11 11-28 11t-28-11q-11-11-11-28t11-28l184-184q12-12 28-12t28 12l184 184q11 11 11 28t-11 28q-11 11-28 11t-28-11L480-528Z"></path>
                        </svg>
                    </a>

                    <h2 v-else class="h4 tabs-heading">{{ item.heading }}</h2>
                </template>

                <Collapse v-if="item.submenu" :id="`sidebar-submenu-${item.heading}`" :open="!!activeGroups[item.heading!]">
                    <ul class="tabs list-unstyled display-row tabs-submenu">
                        <li
                            v-for="child in item.children"
                            :key="child.routeNames[0]"
                            v-require-scope="child.requireScope"
                            v-feature="child.feature"
                            class="sidebar-child"
                            :class="{ active: isCurrent(child.routeNames) }">
                            <router-link
                                :to="{ name: child.routeNames[0], params: props.params }"
                                :aria-current="isCurrent(child.routeNames) ? 'page' : undefined">
                                {{ child.label }}
                            </router-link>
                        </li>
                    </ul>
                </Collapse>
                <ul v-else :id="`sidebar-submenu-${item.heading}`" class="tabs list-unstyled display-row">
                    <li
                        v-for="child in item.children"
                        :key="child.routeNames[0]"
                        v-require-scope="child.requireScope"
                        v-feature="child.feature"
                        class="sidebar-child"
                        :class="{ active: isCurrent(child.routeNames) }">
                        <router-link :to="{ name: child.routeNames[0], params: props.params }" :aria-current="isCurrent(child.routeNames) ? 'page' : undefined">
                            {{ child.label }}
                        </router-link>
                    </li>
                </ul>
            </li>
            <li v-else v-require-scope="item.requireScope" v-feature="item.feature" :class="{ active: item.routeNames && isCurrent(item.routeNames) }">
                <router-link
                    :to="{ name: item.routeNames && item.routeNames[0], params: props.params }"
                    :aria-current="item.routeNames && isCurrent(item.routeNames) ? 'page' : undefined"
                    :class="{ 'd-flex align-items-start gap-xs': item.icon }">
                    <svg
                        v-if="item.icon"
                        class="sidebar-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        :width="iconSize"
                        :height="iconSize"
                        viewBox="0 -960 960 960"
                        fill="currentColor"
                        aria-hidden="true">
                        <path :d="item.icon"></path>
                    </svg>
                    {{ item.label }}
                </router-link>
            </li>
        </template>
    </ul>
</template>

<style scoped>
    li.active:has(.tabs-submenu) a {
        pointer-events: all;
        cursor: pointer !important;
    }

    .sidebar-chevron {
        color: var(--text-color);
        opacity: 0.5;
        transition: transform 0.2s ease-in-out;
    }

    .sidebar-chevron.collapsed {
        transform: rotate(90deg);
    }

    /* Visibility of the submenu is driven by Collapse (v-show), not the li.active/tabs-submenu display toggle in tabs.less */
    :deep(.tabs.tabs-submenu) {
        display: block;
    }

    /* Hide a group when v-require-scope/v-feature have hidden all of its children (they set an inline display: none) */
    .sidebar-group:not(:has(.sidebar-child:not([style*='none']))) {
        display: none !important;
    }

    /* Centre the icon on the first line of text; wrapped lines grow below */
    .sidebar-icon {
        color: var(--brand-secondary-500);
        flex-shrink: 0;
        margin-top: calc((1lh - 18px) / 2);
    }
</style>
